/**
 * GlobalLoadingBar — NProgress-style top loading bar for API requests
 *
 * Hooks into businessApi interceptors to show/hide automatically.
 * Renders a thin animated bar at the top of the viewport during requests.
 *
 * Mount once in Layout. Completely headless — no props needed.
 *
 * Usage:
 *   import { GlobalLoadingBar } from '@/components/ui/GlobalLoadingBar';
 *   // In Layout: <GlobalLoadingBar />
 */

import React, { useEffect, useRef, useState } from 'react';
import { zIndex } from '@/tokens/zIndex';

let activeRequests = 0;
const listeners = new Set<(loading: boolean) => void>();

/** Call from API interceptors to track active requests */
export function startLoading() {
  activeRequests++;
  if (activeRequests === 1) {
    listeners.forEach((fn) => fn(true));
  }
}

export function stopLoading() {
  activeRequests = Math.max(0, activeRequests - 1);
  if (activeRequests === 0) {
    listeners.forEach((fn) => fn(false));
  }
}

export const GlobalLoadingBar: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const listener = (isLoading: boolean) => setLoading(isLoading);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setProgress(10);
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          // Slow asymptotic approach to 90%
          if (prev >= 90) return prev;
          return prev + (90 - prev) * 0.1;
        });
      }, 300);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (progress > 0) {
        setProgress(100);
        // Reset after animation completes
        const timeout = setTimeout(() => setProgress(0), 400);
        return () => clearTimeout(timeout);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (progress === 0) return null;

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Loading"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: zIndex.skipNav,
        pointerEvents: 'none',
        opacity: progress === 100 ? 0 : 1,
        transition: 'opacity 0.4s ease',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: 'rgb(var(--color-primary, 99 102 241))',
          transition: loading ? 'width 0.3s ease' : 'width 0.2s ease',
          boxShadow: '0 0 8px rgb(var(--color-primary, 99 102 241) / 0.4)',
        }}
      />
    </div>
  );
};

export default GlobalLoadingBar;
